import { useCookies as _useCookies } from 'vue3-cookies'

type SameSite = 'Lax' | 'Strict' | 'None'

type CookieOptions = {
  expires: string
  path: string
  domain: string
  secure: string | boolean
  sameSite: SameSite
  partitioned: boolean
}

type CookieStore = {
  set: (
    name: string,
    value: unknown,
    expires: string,
    path: string,
    domain: string,
    secure: string | boolean,
    sameSite: SameSite,
    partitioned: boolean
  ) => void
  get: (name: string) => unknown
  remove: (name: string) => boolean
  keys: () => string[]
  isKey: (name: string) => boolean
}

type UseCookiesResult = {
  setCookie: (name: string, value: unknown, config?: Partial<CookieOptions>) => void
  getCookie: (name: string) => unknown
  getAllCookies: () => string[]
  removeCookie: (name: string) => boolean
  isCookieExists: (name: string) => boolean
  removeAllCookies: () => void
}

export const cookieDefaults: CookieOptions = {
  expires: '1d',
  path: '/',
  domain: '',
  secure: '',
  sameSite: 'Lax',
  partitioned: false,
}

export function useCookies (): UseCookiesResult {
  const { cookies: $cookies } = _useCookies() as { cookies: CookieStore }

  const setCookie: UseCookiesResult['setCookie'] = (name, value, config = {}) => {
    const { expires, path, domain, secure, sameSite, partitioned } = {
      ...cookieDefaults,
      ...config,
    }

    $cookies.set(
      name,
      value,
      expires,
      path,
      domain,
      secure,
      sameSite,
      partitioned
    )
  }

  const getCookie: UseCookiesResult['getCookie'] = (name) => {
    return $cookies.get(name)
  }

  const getAllCookies: UseCookiesResult['getAllCookies'] = () => {
    return $cookies.keys()
  }

  const removeCookie: UseCookiesResult['removeCookie'] = (name) => {
    return $cookies.remove(name)
  }

  const isCookieExists: UseCookiesResult['isCookieExists'] = (name) => {
    return $cookies.isKey(name)
  }

  const removeAllCookies: UseCookiesResult['removeAllCookies'] = () => {
    $cookies.keys().forEach((cookie) => {
      $cookies.remove(cookie)
    })
  }

  return {
    setCookie,
    getCookie,
    getAllCookies,
    removeCookie,
    isCookieExists,
    removeAllCookies,
  }
}
